import type { ApiItem, BasketItem } from '@virginexperiencedays/basket';
import { updateBasketItem } from '@virginexperiencedays/basket/src/index';
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { mapBasketItem, mapBasketItems } from './mapBasketItems';

type ContextType = {
  isUpdatingBasket: boolean;
  isLoading: boolean;
  promoCodeSuccess: boolean;
  error: string | null;
  basketItems: ApiItem[];
  activePromoCode: string;
  minBasketItems: number;
  maxBasketItems: number;
  handleSetIsLoading: (isLoading: boolean) => void;
  handleSetError: (error: string | null) => void;
  handleSetBasketItems: (
    basketItems: ApiItem[],
    data?: { removedItem?: BasketItem | null; totalAmount?: number },
  ) => void;
  handleSetBasketItemQuantity: (id: string, qty: number) => void;
  handleSetActivePromoCode: (code: string | null) => void;
  setPromoCodeSuccess: Dispatch<SetStateAction<boolean>>;
};

const initialState: ContextType = {
  isUpdatingBasket: false,
  isLoading: false,
  promoCodeSuccess: false,
  error: null,
  basketItems: [],
  activePromoCode: '',
  minBasketItems: 0,
  maxBasketItems: 9,
  handleSetBasketItems: () => null,
  handleSetBasketItemQuantity: () => null,
  handleSetIsLoading: () => null,
  handleSetError: () => null,
  handleSetActivePromoCode: () => null,
  setPromoCodeSuccess: () => null,
};

const QuickBasketContext = createContext<ContextType>(initialState);

export type Props = {
  children: ReactNode;
  setQuickBasketOpen?: (open: boolean) => void;
  handleQuickBasketItemCount?: (count: number) => void;
  callbackSetBasketItems?: (data: {
    basketItems?: BasketItem[];
    removedItem?: BasketItem | null;
    totalAmount?: number;
  }) => void;
};

const QuickBasketContextProvider = ({
  setQuickBasketOpen,
  handleQuickBasketItemCount,
  children,
  callbackSetBasketItems,
}: Props) => {
  const [isUpdatingBasket, setIsUpdatingBasket] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [basketItems, setBasketItems] = useState<ApiItem[]>();
  const [activePromoCode, setActivePromoCode] = useState<string>('');
  const [promoCodeSuccess, setPromoCodeSuccess] = useState(false);

  const handleSetIsLoading = (isLoading: boolean) => {
    setIsLoading(isLoading);
  };

  const handleSetError = (error: string | null) => {
    const errorMessage = error ? `[QuickBasket Error]: ${error}` : null;
    console.error(errorMessage);
    setError(errorMessage);
  };

  // set basket items and dispatch event to update basket count
  const handleSetBasketItems = (
    basketItems: ApiItem[],
    { removedItem, totalAmount }: { removedItem?: BasketItem | null; totalAmount?: number } = {},
  ) => {
    if (Array.isArray(basketItems)) {
      setBasketItems(basketItems);
      !basketItems.length && setActivePromoCode('');
      const basketCount = basketItems.reduce((acc, item) => acc + (item.Qty || 0), 0);
      handleQuickBasketItemCount?.(basketCount);

      // callback
      callbackSetBasketItems?.({
        removedItem,
        basketItems: mapBasketItems(basketItems),
        totalAmount,
      });

      // ! The below will not be required when we transition fully to the new header
      const event = new CustomEvent('updateBasketCount', { detail: { basketCount } });
      document.dispatchEvent(event);
    } else {
      console.error('basketItems must be an array');
    }
  };

  const handleSetBasketItemQuantity = async (id: string, qty: number) => {
    try {
      const currentItem = Array.isArray(basketItems)
        ? basketItems.find((item) => id === item?.Id)
        : null;
      const currentBasketItem = currentItem ? mapBasketItem(currentItem) : null;
      const removedItem = qty === 0 ? currentBasketItem : null;

      setIsUpdatingBasket(true);
      const {
        BasketItemTotal: totalAmount,
        Items: items,
      }: { BasketItemTotal: number; Items: ApiItem[] } = await updateBasketItem(
        id,
        qty,
        null,
        null,
        true, // return full basket data
      );
      handleSetBasketItems(items, { removedItem, totalAmount });

      // close the basket when removing the last product
      if (!items?.length) setQuickBasketOpen(false);
    } catch (error) {
      console.error(error instanceof Error ? error.message : 'Unknown error setting basket qty');
    } finally {
      setIsUpdatingBasket(false);
    }
  };

  const handleSetActivePromoCode = (code: string) => {
    setActivePromoCode(code);
  };

  const { minBasketItems, maxBasketItems } = initialState;

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding handlers causes another error - changes on every re-render and should not be used as a hook dependency ()
  const value = useMemo(
    () => ({
      /** state values */
      basketItems,
      error,
      isLoading,
      isUpdatingBasket,
      promoCodeSuccess,
      maxBasketItems,
      minBasketItems,
      activePromoCode,
      /** handlers */
      handleSetBasketItemQuantity,
      handleSetBasketItems,
      handleSetError,
      handleSetIsLoading,
      handleSetActivePromoCode,
      setPromoCodeSuccess,
    }),
    [
      /** ensure state values are included in the deps array */
      basketItems,
      error,
      isLoading,
      isUpdatingBasket,
      promoCodeSuccess,
      maxBasketItems,
      minBasketItems,
      activePromoCode,
    ],
  );

  return <QuickBasketContext.Provider value={value}>{children}</QuickBasketContext.Provider>;
};

const useQuickBasketContext = () => useContext(QuickBasketContext);

export { QuickBasketContextProvider, useQuickBasketContext };
